/** A left-panel keyboard shortcut, resolved from a keydown event. */
export type ToolShortcut = 'exit-shape-creation' | 'select' | 'type' | 'pen'

export interface ResolveToolShortcutOptions {
  isPanModeActive: boolean
  /** A shape preset or pen tool is currently chosen (shape creation mode). */
  hasShapeCreationState: boolean
}

/** True when the event target is somewhere the user types (inputs, textareas, text editors). */
export function isEditableTarget(target: HTMLElement | null): boolean {
  return (
    !!target &&
    (target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.isContentEditable ||
      target.getAttribute('role') === 'textbox')
  )
}

/**
 * Keyboard shortcuts:
 * - Escape exits shape creation mode and returns to Select tool
 * - "V" selects the Select tool (mirrors common editor shortcut)
 * - "T" selects the Type tool
 * - "P" activates the pen tool
 *
 * Returns `null` when the keystroke isn't a shortcut the left panel handles.
 */
export function resolveToolShortcut(
  e: KeyboardEvent,
  { isPanModeActive, hasShapeCreationState }: ResolveToolShortcutOptions
): ToolShortcut | null {
  const { key } = e

  // Don't steal keystrokes when typing.
  if (isEditableTarget(e.target as HTMLElement | null)) return null

  // Only handle unmodified shortcuts.
  if (e.ctrlKey || e.metaKey || e.altKey) return null

  if (key === 'Escape' && isPanModeActive) return null

  if (key === 'Escape' && hasShapeCreationState) return 'exit-shape-creation'

  if (key === 'v' || key === 'V') return 'select'

  if (key === 't' || key === 'T') return 'type'

  if (key === 'p' || key === 'P') return 'pen'

  return null
}
